import React, { useEffect, useCallback } from "react"
import hotkeys from "hotkeys-js"

import { TwoColumnContent } from "~layouts/ReferenceLayout"
import { render, astify } from "~utils/rehype-render"

import {
  Output, Button, Control, Alert,
} from "~components/Explorer"
import ResponseExample from "~components/ResponseExample"
import { navigate } from "gatsby"
import Pill from "~components/Pill"
import TooltipWrapper from "~components/TooltipWrapper"
import Message from "~components/Message"
import * as style from "./Endpoint.module.scss"
import VersionDropdown from "../ApiVersioning/VersionDropdown"
import UrlBar from "./UrlBar"
import Navigation from "./Navigation"
import Request from "./Request"
import Responses from "./Responses"
import CodeSamples from "./CodeSamples"
import RelatedGuides from "./RelatedGuides"
import {
  isBaseVersion, getUrlVersionPart, compareVersions, VERSION_INFO,
} from '../../utils/versioning'

const Endpoint = ({ explorer, endpoint, microcopy }) => {
  useEffect(() => {
    hotkeys("command+e,ctrl+e", (event) => {
      event.preventDefault()
      explorer.enable()
    })
  }, [])

  const onSelectVersion = useCallback(
    (version) => {
      if (version === VERSION_INFO) {
        navigate("/guides/api-calls/api-versioning-strategy")
        return
      }
      const urlVersionPart = getUrlVersionPart(version)
      navigate(`/reference/${urlVersionPart}${endpoint?.operationGroup?.replace(/_/g, '-')?.replace(/#/g, '--')}/`)
    },
    [endpoint],
  )
  const isLatestVersion = endpoint.version === endpoint.versions
    // TODO get info about whether version is stable // .filter((v) => v.stabilityLevel !== "beta")
    .sort(compareVersions)[0]

  return (
    <div className={style.reference}>
      <TwoColumnContent>
        <>
          <Alert explorer={explorer} />
          <Button explorer={explorer} endpoint={endpoint} offset="extra" />
          { endpoint.stabilityLevel === "beta"
            && (
              <TooltipWrapper
                id="beta-pill"
                content={microcopy.text("reference.tooltip.beta")}
              >
                <Pill
                  pillType="pillLarge"
                  text={microcopy.text("reference.pill.beta")}
                  color="blue"
                />
              </TooltipWrapper>
            )}
          { endpoint.stabilityLevel !== "beta" && isLatestVersion && !endpoint.deprecated
            && (
              <TooltipWrapper
                id="latest-version-pill"
                content={microcopy.text("reference.tooltip.latest_version")}
              >
                <Pill
                  pillType="pillLarge"
                  text={microcopy.text("reference.pill.latest_version")}
                  color="grey"
                />
              </TooltipWrapper>
            )}
          { endpoint.deprecated === true
            && (
              <TooltipWrapper
                id="deprecated-pill"
                content={microcopy.text("reference.tooltip.deprecated")}
              >
                <Pill
                  pillType="pillLarge"
                  text={microcopy.text("reference.pill.deprecated")}
                  color="orange"
                />
              </TooltipWrapper>
            )}
          <h1>
            {endpoint.title}
          </h1>
          <VersionDropdown
            addon
            microcopy={microcopy}
            version={endpoint.version}
            versions={endpoint.versions}
            onSelectVersion={onSelectVersion}
          />
          <Navigation item={endpoint} microcopy={microcopy} />
          <UrlBar endpoint={endpoint} />
          {isBaseVersion(endpoint.version) && (
            <Message
              type="notice"
            >
              {render(astify(microcopy.text("versions.base_version_notice.__raw__.endpoint")))}
            </Message>
          )}
          {endpoint.message && (
            <Message
              type={endpoint.messageType}
            >
              {render(endpoint.messageAst)}
            </Message>
          )}
          <div className={style.summary}>{render(endpoint.descriptionAst)}</div>
          <RelatedGuides
            guides={endpoint.relatedGuides}
            microcopy={microcopy}
          />
          <Request
            explorer={explorer}
            endpoint={endpoint}
            microcopy={microcopy}
          />
          <Responses
            microcopy={microcopy}
            explorer={explorer}
            endpoint={endpoint}
          />
          <Control explorer={explorer} />
        </>
        <div className={style.examples}>
          <CodeSamples endpoint={endpoint} microcopy={microcopy} />
          <Output explorer={explorer} />
          {endpoint.defaultResponse?.defaultExampleJson ? (
            <ResponseExample
              code={endpoint.defaultResponse.defaultExampleJson}
            />
          ) : (
            endpoint.defaultResponse?.resources?.[0] && (
              <ResponseExample
                code={endpoint.defaultResponse.resources[0].jsonExample}
              />
            )
          )}
        </div>
      </TwoColumnContent>
    </div>
  )
}

export default Endpoint
export { style }
